import { useSelector } from 'react-redux'
import {
  Box,
  Chip,
  Typography,
} from '@mui/material'
import FiberManualRecordRoundedIcon from '@mui/icons-material/FiberManualRecordRounded'
import CardWithSpace from '../../../components/Cards/CardWithSpace/index.jsx'
import { selectCompanies } from '../../../redux/features/companies/companiesSlice'
import UseTranslation from '../../../hooks/UseTranslation.jsx'
import IdentityProvider from './IdentityProvider.jsx'
import ServiceProvider from './ServiceProvider.jsx'
import TitleWithIcon from '../../../components/Cards/helpers/TitleWithIcon.jsx'
import { ReactComponent as LockIcon } from '../../../images/lock.svg'
import SvgIconSize from '../../../components/Cards/helpers/SvgIconSize.jsx'

const SsoConfiguration = ({
  companyDetails,
  samlCompany,
}) => {
  const { t } = UseTranslation()
  const ssoCompanyConfig = useSelector(selectCompanies)?.ssoCompanyConfig
  const configCompanyId = Number(ssoCompanyConfig?.company_id)
  const pageCompanyId = Number(companyDetails?.id)
  const companyScopedSsoConfig = configCompanyId === pageCompanyId
    ? ssoCompanyConfig
    : null
  const ssoSettingsSnapshot = companyScopedSsoConfig || {}

  const subtitle = ssoSettingsSnapshot.domain
  const statusTitle = samlCompany
    ? (t?.SsoConfigStatusActiveTitle)
    : (t?.SsoConfigStatusInactiveTitle)

  const ssoConnectionEnabled = Boolean(ssoSettingsSnapshot.enabled)

  const statusHeroCardStyles = samlCompany
    ? styles.statusHeroCardActive
    : styles.statusHeroCard

  return (
    <Box sx={styles.sectionGroup}>
      <TitleWithIcon title={t?.['SSO CONFIGURATION']} icon={<LockIcon />} iconSize="sm" customStyle={styles.sectionOverline} />
          <CardWithSpace customStyles={statusHeroCardStyles}>
            <Box sx={styles.statusHeroRow}>
              <Box sx={styles.statusHeroLeft}>
                <Box sx={samlCompany ? styles.statusIconWrapActive : styles.statusIconWrap}>
                 <SvgIconSize icon={<LockIcon />} size="xl" />
                </Box>
                <Box sx={styles.statusTextCol}>
                  <Typography sx={samlCompany ? styles.statusTitleActive : styles.statusTitle}>
                    {statusTitle}
                  </Typography>
                  <Typography sx={styles.statusSubtitle}>
                    {subtitle}
                  </Typography>
                </Box>
              </Box>
              <Chip
                size="small"
                icon={(
                  <FiberManualRecordRoundedIcon
                    sx={styles.statusDotIcon(ssoConnectionEnabled ? '#29CC8B' : '#92929d')}
                  />
                )}
                label={
                  ssoConnectionEnabled
                    ? (t?.SsoConfigConnected)
                    : (t?.SsoConfigNotConnected)
                }
                sx={ssoConnectionEnabled ? styles.stateBadgePositive : styles.stateBadgeNeutral}
              />
            </Box>
          </CardWithSpace>

          <IdentityProvider
            companyScopedSsoConfig={companyScopedSsoConfig}
            ssoSettingsSnapshot={ssoSettingsSnapshot}
          />
          <ServiceProvider ssoSettingsSnapshot={ssoSettingsSnapshot} />

    </Box>
  )
}

const cardShellBase = {
  border: '1px solid #e8eaed',
  borderRadius: '12px',
  boxShadow: 'none',
  marginBottom: 0,
  overflow: 'hidden',
  padding: 0,
}

const styles = {
  sectionGroup: {
    width: '100%',
    mb: { xs: 2.5, sm: 3 },
  },
  sectionOverline: {
    fontSize: '0.6875rem',
    fontWeight: 600,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: '#92929d',
    lineHeight: 1.3,
    mb: 1.25,
    '& svg': {
      stroke: '#92929d',
    },
  },
  statusHeroCard: {
    ...cardShellBase,
  },
  statusHeroCardActive: {
    ...cardShellBase,
    backgroundColor: 'rgba(41, 204, 139, 0.08)',
  },
  statusHeroRow: {
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    alignItems: { xs: 'flex-start', sm: 'center' },
    justifyContent: { xs: 'flex-start', sm: 'space-between' },
    gap: 2,
    px: { xs: 2.5, sm: 3 },
    py: { xs: 2.5, sm: 2.75 },
  },
  statusHeroLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    minWidth: 0,
  },
  statusIconWrap: {
    width: 48,
    height: 48,
    borderRadius: '10px',
    flexShrink: 0,
    bgcolor: 'rgba(146, 146, 157, 0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  statusIconWrapActive: {
    width: 48,
    height: 48,
    borderRadius: '10px',
    flexShrink: 0,
    bgcolor: 'rgba(41, 204, 139, 0.22)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  statusTextCol: {
    minWidth: 0,
  },
  statusTitle: {
    fontWeight: 700,
    fontSize: { xs: '0.95rem', sm: '1.05rem' },
    color: '#1b2559',
    lineHeight: 1.35,
  },
  statusTitleActive: {
    fontWeight: 700,
    fontSize: { xs: '0.95rem', sm: '1.05rem' },
    color: '#1a8f5f',
    lineHeight: 1.35,
  },
  statusSubtitle: {
    mt: 0.5,
    fontSize: '0.8125rem',
    color: '#92929d',
    lineHeight: 1.45,
    wordBreak: 'break-word',
  },
  statusDotIcon: (color) => ({
    '&&': {
      color: `${color} !important`,
      fontSize: 14,
    },
  }),
  stateBadgePositive: {
    height: 28,
    alignSelf: { xs: 'flex-end', sm: 'center' },
    fontWeight: 600,
    fontSize: '0.75rem',
    color: '#29CC8B',
    backgroundColor: 'rgba(41, 204, 139, 0.12)',
    border: '1px solid #29CC8B',
    borderRadius: '999px',
    '& .MuiChip-icon': { marginLeft: '8px' },
    '& .MuiChip-label': { px: 1 },
  },
  stateBadgeNeutral: {
    height: 28,
    alignSelf: { xs: 'flex-end', sm: 'center' },
    fontWeight: 600,
    fontSize: '0.75rem',
    color: '#696974',
    backgroundColor: 'rgba(146, 146, 157, 0.12)',
    border: '1px solid #d0d0d6',
    borderRadius: '999px',
    '& .MuiChip-icon': { marginLeft: '8px' },
    '& .MuiChip-label': { px: 1 },
  },
}

export default SsoConfiguration
